import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DataObjectIcon from '@mui/icons-material/DataObject';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Paper,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { DetailCard } from '../../../components/ui/DetailCard';
import { FieldGrid, type VisibleField } from '../../../components/ui/FieldGrid';
import { ApiError } from '../../../lib/http/ApiError';
import { useNotification } from '../../notifications/hooks/useNotification';
import { EventInboxStatusBadge } from '../components/EventInboxStatusBadge';
import { useGetEventInboxById } from '../hooks/useGetEventInboxById';
import { formatDateTime } from '../../../lib/date/formatDateTime';
import type { EventInboxDetail } from '../eventInboxTypes';

/** Serializes a payload (object or raw JSON string) to pretty JSON for display/copy. */
function stringifyPayload(payload: EventInboxDetail['payload']): string {
  if (typeof payload === 'string') {
    try {
      return JSON.stringify(JSON.parse(payload), null, 2);
    } catch {
      // Not valid JSON — show the raw string rather than crashing.
      return payload;
    }
  }
  return JSON.stringify(payload, null, 2);
}

/** Read-only Event Inbox detail. Route: `/event-inbox/:id`. */
export function EventInboxDetailView() {
  const theme = useTheme();
  const { t } = useTranslation('eventInbox');
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { notifySuccess, notifyError } = useNotification();

  const { data, loading, error } = useGetEventInboxById(id);

  const payloadString = useMemo(() => (data ? stringifyPayload(data.payload) : ''), [data]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(payloadString);
      notifySuccess(t('detail.payloadCopied'));
    } catch {
      notifyError(new Error(t('detail.payloadCopyFailed')));
    }
  };

  const containerSx = {
    px: 3,
    pt: 3,
    pb: 4,
    bgcolor: theme.palette.grey[50],
    minHeight: `calc(100vh - ${theme.toolbar.height}px)`,
    overflowY: 'auto',
  } as const;

  const backButton = (
    <Button
      startIcon={<ArrowBackIcon />}
      onClick={() => navigate(-1)}
      sx={{ textTransform: 'none', color: theme.palette.primary.main, mb: 2 }}
    >
      {t('detail.back')}
    </Button>
  );

  if (loading) {
    return (
      <Box sx={containerSx}>
        {backButton}
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  // 404 (or any load failure) → clean error state
  if (error || !data) {
    const notFound = ApiError.isApiError(error) && error.isNotFound;
    return (
      <Box sx={containerSx}>
        {backButton}
        <Alert severity="error">{notFound ? t('detail.notFound') : t('errors.loadError')}</Alert>
      </Box>
    );
  }

  const metadataFields: VisibleField[] = [
    { id: 'id', label: t('detail.fields.id'), value: data.id },
    { id: 'vhost', label: t('detail.fields.vhost'), value: data.vhost || null },
    { id: 'exchange', label: t('detail.fields.exchange'), value: data.exchange || null },
    { id: 'failed_rule', label: t('detail.fields.failedRule'), value: data.failed_rule || null },
    { id: 'attempts', label: t('detail.fields.attempts'), value: String(data.attempts) },
    {
      id: 'received_at',
      label: t('detail.fields.receivedAt'),
      value: formatDateTime(data.received_at),
    },
    {
      id: 'processed_at',
      label: t('detail.fields.processedAt'),
      value: data.processed_at ? formatDateTime(data.processed_at) : null,
    },
  ];

  return (
    <Box sx={containerSx}>
      {backButton}

      {/* Title: status badge + monospace message_id */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3, flexWrap: 'wrap' }}>
        <EventInboxStatusBadge status={data.status} size="medium" />
        <Typography
          sx={{
            fontFamily: 'monospace',
            fontSize: '1.05rem',
            fontWeight: 600,
            color: theme.palette.text.body,
            wordBreak: 'break-all',
          }}
        >
          {data.message_id || '—'}
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* Metadata */}
        <DetailCard
          icon={
            <InfoOutlinedIcon
              sx={{ fontSize: theme.iconSize.sm, color: theme.palette.primary.main }}
            />
          }
          iconBg={alpha(theme.palette.primary.main, 0.1)}
          title={t('detail.section.metadata')}
        >
          <FieldGrid fields={metadataFields} />
        </DetailCard>

        {/* Error */}
        <DetailCard
          icon={
            <ErrorOutlineIcon
              sx={{ fontSize: theme.iconSize.sm, color: theme.palette.error.main }}
            />
          }
          iconBg={alpha(theme.palette.error.main, 0.1)}
          title={t('detail.section.error')}
        >
          {data.last_error ? (
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                bgcolor: alpha(theme.palette.error.main, 0.1),
                borderColor: alpha(theme.palette.error.main, 0.25),
                fontFamily: 'monospace',
                fontSize: '0.85rem',
                color: theme.palette.error.dark,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {data.last_error}
            </Paper>
          ) : (
            <Typography sx={{ color: theme.palette.text.label }}>—</Typography>
          )}
        </DetailCard>

        {/* Payload — collapsed by default */}
        <Accordion
          disableGutters
          elevation={0}
          defaultExpanded={false}
          sx={{
            borderRadius: 3,
            border: `1px solid ${theme.palette.divider}`,
            '&:before': { display: 'none' },
            overflow: 'hidden',
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="event-inbox-payload"
            id="event-inbox-payload-header"
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <DataObjectIcon fontSize="small" sx={{ color: theme.palette.primary.main }} />
              <Typography sx={{ fontWeight: 600, color: theme.palette.text.body }}>
                {t('detail.section.payload')}
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails id="event-inbox-payload" sx={{ pt: 0 }}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
              <Tooltip title={t('detail.copyPayload')}>
                <IconButton onClick={handleCopy} size="small" aria-label={t('detail.copyPayload')}>
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            <Box
              component="pre"
              sx={{
                m: 0,
                p: 2,
                bgcolor: theme.palette.grey[900],
                color: theme.palette.common.white,
                borderRadius: 2,
                fontFamily: 'monospace',
                fontSize: '0.8rem',
                lineHeight: 1.6,
                maxHeight: 400,
                overflowY: 'auto',
                whiteSpace: 'pre',
              }}
            >
              {payloadString}
            </Box>
          </AccordionDetails>
        </Accordion>
      </Box>
    </Box>
  );
}
